import MungPayUtil from '../../../core/utils/LogUtil';
/**
 * MungPay Service
 * 비즈니스 로직과 데이터 검증을 담당
 * Repository 인터페이스에 의존
 */
export class MungPayService {
  constructor(mungPayRepository) {
    if (!mungPayRepository) {
      throw new Error('MungPayRepository is required');
    }
    this.mungPayRepository = mungPayRepository;
  }

  /**
   * 보험 청구 생성
   * @param {Object} claimData - 청구 데이터
   * @param {string} claimData.medical_receipt_url - 의료비 영수증 URL (필수)
   * @returns {Promise<BaseResponse<any>>}
   */
  async createClaim(url) {
    try {
      MungPayUtil.log('🏗️ [MungPay Service] 보험 청구 생성 시작');
      MungPayUtil.log('📊 [MungPay Service] 입력 데이터:', url);

      MungPayUtil.log('✅ [MungPay Service] 데이터 검증 완료');

      // Repository 호출
      const result = await this.mungPayRepository.createClaim(url);

      MungPayUtil.log('✅ [MungPay Service] 보험 청구 생성 완료');
      return result;
    } catch (error) {
      MungPayUtil.error('❌ [MungPay Service] 보험 청구 생성 실패:', error);
      throw error;
    }
  }

  /**
   * 전화번호 인증
   * @param {string} claimId - 청구 ID
   * @param {Object} phoneData - 전화번호 데이터
   * @param {string} phoneData.phone_number - 전화번호 (필수)
   * @returns {Promise<BaseResponse<any>>}
   */
  async verifyPhone(claimId, phoneNumber) {
    try {
      MungPayUtil.log('🏗️ [MungPay Service] 전화번호 인증 시작');
      MungPayUtil.log('📊 [MungPay Service] 전화번호:', phoneNumber);

      // 전화번호 형식 검증 (간단한 검증)
      const phoneRegex = /^01[016789]-?\d{3,4}-?\d{4}$/;
      const cleanPhone = phoneNumber.replace(/[^0-9]/g, '');
      if (!phoneRegex.test(phoneNumber) && cleanPhone.length !== 11) {
        throw new Error('올바른 전화번호 형식이 아닙니다. (예: 010-1234-5678)');
      }

      MungPayUtil.log('✅ [MungPay Service] 데이터 검증 완료');

      // Repository 호출
      const result = await this.mungPayRepository.verifyPhone(
        claimId,
        phoneNumber
      );

      MungPayUtil.log('✅ [MungPay Service] 전화번호 인증 완료');
      return result;
    } catch (error) {
      MungPayUtil.error('❌ [MungPay Service] 전화번호 인증 실패:', error);
      throw error;
    }
  }

  /**
   * 청구 상태 조회
   * @param {string} claimNumber - 청구 번호 (예: MP2025080163EC1C)
   * @returns {Promise<BaseResponse<any>>}
   */
  async getClaimStatus(claimNumber) {
    try {
      MungPayUtil.log('🏗️ [MungPay Service] 청구 상태 조회 시작');
      MungPayUtil.log('📊 [MungPay Service] 청구 번호:', claimNumber);

      // 청구 번호 검증
      if (!claimNumber) {
        throw new Error('청구 번호가 필요합니다.');
      }

      if (typeof claimNumber !== 'string' || claimNumber.trim() === '') {
        throw new Error('유효한 청구 번호를 입력해주세요.');
      }

      // 청구 번호 형식 검증 (MP로 시작하는 형식)
      if (!claimNumber.startsWith('MP')) {
        throw new Error(
          '청구 번호는 MP로 시작해야 합니다. (예: MP2025080163EC1C)'
        );
      }

      if (claimNumber.length < 10) {
        throw new Error('청구 번호가 너무 짧습니다.');
      }

      MungPayUtil.log('✅ [MungPay Service] 데이터 검증 완료');

      // Repository 호출
      const result = await this.mungPayRepository.getClaimStatus(claimNumber);

      MungPayUtil.log('✅ [MungPay Service] 청구 상태 조회 완료');
      return result;
    } catch (error) {
      MungPayUtil.error('❌ [MungPay Service] 청구 상태 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 청구 결과 조회
   * @param {String} claimId - 청구 ID (예: 85)
   * @returns {Promise<BaseResponse<any>>}
   */
  async getClaimResult(claimId) {
    try {
      MungPayUtil.log('🏗️ [MungPay Service] 청구 결과 조회 시작');
      MungPayUtil.log('📊 [MungPay Service] 청구 ID:', claimId);

      // 청구 ID 검증
      if (!claimId) {
        throw new Error('청구 ID가 필요합니다.');
      }

      MungPayUtil.log('✅ [MungPay Service] 데이터 검증 완료');

      // Repository 호출
      const result = await this.mungPayRepository.getClaimResult(claimId);

      MungPayUtil.log('✅ [MungPay Service] 청구 결과 조회 완료');
      return result;
    } catch (error) {
      MungPayUtil.error('❌ [MungPay Service] 청구 결과 조회 실패:', error);
      throw error;
    }
  }

  /**
   * S3 파일 업로드 (Presigned URL 방식)
   * @param {File} file - 업로드할 파일 객체 (필수)
   * @param {string} expiration - URL 만료 시간 (분 단위, 기본값: '60')
   * @returns {Promise<Object>} 업로드 결과 객체
   */
  async uploadToS3(file, expiration = '60') {
    try {
      MungPayUtil.log('🏗️ [MungPay Service] S3 파일 업로드 시작');
      MungPayUtil.log('📊 [MungPay Service] 파일명:', file?.name);
      MungPayUtil.log('📊 [MungPay Service] 파일 타입:', file?.type);
      MungPayUtil.log('📊 [MungPay Service] 파일 크기:', file?.size);
      MungPayUtil.log('📊 [MungPay Service] 만료 시간:', expiration);

      // 필수 파라미터 검증
      if (!file) {
        throw new Error('파일이 필요합니다.');
      }

      if (!(file instanceof File)) {
        throw new Error('유효한 파일 객체가 아닙니다.');
      }

      if (!file.name || file.name.trim() === '') {
        throw new Error('파일명이 필요합니다.');
      }

      if (!file.type || file.type.trim() === '') {
        throw new Error('파일 타입이 필요합니다.');
      }

      if (file.size === 0) {
        throw new Error('빈 파일은 업로드할 수 없습니다.');
      }

      // 파일 크기 제한 (예: 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        throw new Error('파일 크기가 너무 큽니다. (최대 10MB)');
      }

      // 만료 시간 검증 (숫자형 문자열인지 확인)
      if (expiration && isNaN(Number(expiration))) {
        throw new Error('만료 시간은 숫자여야 합니다.');
      }

      // 파일 확장자 검증
      const allowedExtensions = [
        '.jpg',
        '.jpeg',
        '.png',
        '.gif',
        '.pdf',
        '.doc',
        '.docx',
      ];
      const fileExtension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf('.'));
      if (fileExtension && !allowedExtensions.includes(fileExtension)) {
        MungPayUtil.warn(
          '⚠️ [MungPay Service] 지원되지 않는 파일 확장자:',
          fileExtension
        );
      }

      MungPayUtil.log('✅ [MungPay Service] 데이터 검증 완료');

      // Repository 호출
      const result = await this.mungPayRepository.uploadToS3(file, expiration);

      MungPayUtil.log('✅ [MungPay Service] S3 파일 업로드 완료');
      return result;
    } catch (error) {
      MungPayUtil.error('❌ [MungPay Service] S3 파일 업로드 실패:', error);
      throw error;
    }
  }

  /**
   * KB 영수증 분석
   * @param {string} medicalReceiptUrl - 의료비 영수증 이미지 URL (필수)
   * @returns {Promise<BaseResponse<any>>}
   */
  async analyzeKbReceipt(medicalReceiptUrl) {
    try {
      MungPayUtil.log('🏗️ [MungPay Service] KB 영수증 분석 시작');
      MungPayUtil.log('📊 [MungPay Service] 입력 URL:', medicalReceiptUrl);

      // 데이터 검증
      if (!medicalReceiptUrl) {
        throw new Error('의료비 영수증 URL이 필요합니다.');
      }

      // URL 형식 검증
      try {
        new URL(medicalReceiptUrl);
      } catch (urlError) {
        throw new Error('올바른 URL 형식이 아닙니다.');
      }

      // 지원되는 이미지 확장자 검증
      const supportedExtensions = ['.jpg', '.jpeg', '.png', '.pdf'];
      const url = new URL(medicalReceiptUrl);
      const pathname = url.pathname.toLowerCase();
      const hasValidExtension = supportedExtensions.some(
        ext => pathname.includes(ext) || pathname.endsWith(ext)
      );

      if (!hasValidExtension) {
        MungPayUtil.warn(
          '⚠️ [MungPay Service] 지원되지 않는 파일 형식일 수 있음:',
          pathname
        );
      }

      MungPayUtil.log('✅ [MungPay Service] 데이터 검증 완료');

      // Repository 호출
      const result =
        await this.mungPayRepository.analyzeKbReceipt(medicalReceiptUrl);

      MungPayUtil.log('✅ [MungPay Service] KB 영수증 분석 완료');
      return result;
    } catch (error) {
      MungPayUtil.error('❌ [MungPay Service] KB 영수증 분석 실패:', error);
      throw error;
    }
  }

  /**
   * 처방전 분석
   * @param {string} prescriptionUrl - 처방전 이미지 URL (필수)
   * @returns {Promise<BaseResponse<any>>}
   */
  async analyzePrescription(prescriptionUrl) {
    try {
      MungPayUtil.log('🏗️ [MungPay Service] 처방전 분석 시작');
      MungPayUtil.log('📊 [MungPay Service] 입력 URL:', prescriptionUrl);

      // 데이터 검증
      if (!prescriptionUrl) {
        throw new Error('처방전 URL이 필요합니다.');
      }

      // URL 형식 검증
      try {
        new URL(prescriptionUrl);
      } catch (urlError) {
        throw new Error('올바른 URL 형식이 아닙니다.');
      }

      // 지원되는 이미지 확장자 검증
      const supportedExtensions = ['.jpg', '.jpeg', '.png', '.pdf'];
      const url = new URL(prescriptionUrl);
      const pathname = url.pathname.toLowerCase();
      const hasValidExtension = supportedExtensions.some(
        ext => pathname.includes(ext) || pathname.endsWith(ext)
      );

      if (!hasValidExtension) {
        MungPayUtil.warn(
          '⚠️ [MungPay Service] 지원되지 않는 파일 형식일 수 있음:',
          pathname
        );
      }

      MungPayUtil.log('✅ [MungPay Service] 데이터 검증 완료');

      // Repository 호출
      const result =
        await this.mungPayRepository.analyzePrescription(prescriptionUrl);

      MungPayUtil.log('✅ [MungPay Service] 처방전 분석 완료');
      return result;
    } catch (error) {
      MungPayUtil.error('❌ [MungPay Service] 처방전 분석 실패:', error);
      throw error;
    }
  }
}
